import './App.css';
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import EligibilityCheck from './pages/EligibilityCheck';
import Signup from './pages/Signup';
import OTP from './pages/OTP';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import NotQualified from './pages/NotQualified';
import InviteCollab from './pages/InviteCollab';
import InviteAdmin from './pages/InviteAdmin';
import SchoolInfo from './pages/SchoolInfo';
import GrantLandingPage from './pages/GrantLandingPage';
import CIGForm from './pages/CIGForm';
import ILGForm from './pages/ILGForm';
import CIGComplete from './pages/CIGComplete';
import ILGComplete from './pages/ILGComplete';
import AcceptInvite from './pages/AcceptInvite';
import FAQ from './pages/FAQ';
import WinnersAssetPage from './pages/WinnersAssetPage';
import ProjectKickoffRegistration from './pages/ProjectKickoffRegistration';
// Help docs — commented out, restore when ready
// import HelpHome from './pages/HelpHome';
// import HelpArticle from './pages/HelpArticle';
import Layout from './components/Layout';
import useAnalyticsPageTracking from './hooks/useAnalyticsPageTracking';
import { isApplicationClosed } from './utils/applicationGate';

// JWT-based auth guard
import api from './api';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';

// NotFound / fallback handler: redirect users based on auth + profile completion
function NotFoundHandler({ gateClosed }) {
  const navigate = useNavigate();
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          // not logged in -> send to login
          if (mounted) navigate('/login');
          return;
        }
        if (gateClosed) {
          if (mounted) navigate('/winners-assets');
          return;
        }
        // If we have cached info, use it (fast). Otherwise fetch.
        const cached = localStorage.getItem('schoolProfileCompleted');
        if (cached === 'true') {
          if (mounted) navigate('/grantlanding');
          return;
        }
        if (cached === 'false') {
          if (mounted) navigate('/schoolinfo');
          return;
        }
        // fallback: request profile status from server
        try {
          const pr = await api.get('/api/public/school-profile');
          const completed = pr?.data?.exists && (pr.data.status === 'completed' || pr.data.completed === true);
          localStorage.setItem('schoolProfileCompleted', completed ? 'true' : 'false');
          if (mounted) {
            navigate(completed ? '/grantlanding' : '/schoolinfo');
          }
        } catch (err) {
          // if anything goes wrong, send to login
          if (mounted) navigate('/login');
        }
      } catch (e) {
        if (mounted) navigate('/login');
      }
    })();
    return () => { mounted = false; };
  }, [navigate, gateClosed]);
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
      <CircularProgress />
    </Box>
  );
}

const isAuthenticated = () => {
  const token = localStorage.getItem('token');
  return !!token;
};

function ProtectedRoute({ children }) {
  return isAuthenticated() ? children : <Navigate to="/login" />;
}

function AdminRoute({ children }) {
  const [checking, setChecking] = React.useState(true);
  const [allowed, setAllowed] = React.useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const me = await api.get('/api/public/me');
        const isAdmin = me?.data?.authenticated && me.data.role === 'ADMIN';
        if (mounted) {
          setAllowed(isAdmin);
          if (!isAdmin) navigate('/grantlanding');
        }
      } catch (err) {
        if (mounted) {
          setAllowed(false);
          navigate('/login');
        }
      } finally {
        if (mounted) setChecking(false);
      }
    })();
    return () => { mounted = false; };
  }, [navigate]);

  if (checking) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
      <CircularProgress />
    </Box>
  );
  return allowed ? children : null;
}

// Guard: redirect to grantlanding if school profile is completed
function SchoolInfoCompletionRedirect({ children }) {
  const [checking, setChecking] = React.useState(true);
  const [completed, setCompleted] = React.useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const pr = await api.get('/api/public/school-profile');
        const isCompleted = pr?.data?.exists && (pr.data.status === 'completed' || pr.data.completed === true);
        localStorage.setItem('schoolProfileCompleted', isCompleted ? 'true' : 'false');
        if (mounted) {
          setCompleted(isCompleted);
          if (isCompleted) navigate('/grantlanding');
        }
      } catch (err) {
        if (mounted) {
          setCompleted(false);
        }
      } finally {
        if (mounted) setChecking(false);
      }
    })();
    return () => { mounted = false; };
  }, [navigate]);

  if (checking) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
      <CircularProgress />
    </Box>
  );
  return completed ? null : children;
}

// Guard: allow access to grant pages only if school profile is completed
function SchoolInfoEditableGuard({ children }) {
  const [checking, setChecking] = React.useState(true);
  const [allowed, setAllowed] = React.useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const pr = await api.get('/api/public/school-profile');
        const completed = pr?.data?.exists && (pr.data.status === 'completed' || pr.data.completed === true);
        localStorage.setItem('schoolProfileCompleted', completed ? 'true' : 'false');
        if (mounted) {
          setAllowed(completed);
          if (!completed) navigate('/schoolinfo');
        }
      } catch (err) {
        if (mounted) {
          localStorage.setItem('schoolProfileCompleted', 'false');
          setAllowed(false);
          navigate('/schoolinfo');
        }
      } finally {
        if (mounted) setChecking(false);
      }
    })();
    return () => { mounted = false; };
  }, [navigate]);

  if (checking) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
      <CircularProgress />
    </Box>
  );
  return allowed ? children : null;
}

function AnalyticsPageTracker() {
  useAnalyticsPageTracking();
  return null;
}

function App() {
  const gateClosed = isApplicationClosed();

  return (
    <Router>
      <AnalyticsPageTracker />
      <Layout>
        <Routes>
          <Route path="/" element={<EligibilityCheck />} />
          <Route path="/signup" element={gateClosed ? <Navigate to="/" replace /> : <Signup />} />
          <Route path="/otp" element={<OTP />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgotpassword" element={<ForgotPassword />} />
          <Route path="/notqualified" element={<NotQualified />} />
          <Route path="/kickoff/campus-improvement-grant" element={<ProjectKickoffRegistration programType="campus-improvement-grant" />} />
          <Route path="/kickoff/innovation-lounge" element={<ProjectKickoffRegistration programType="innovation-lounge" />} />
          <Route path="/winners-assets" element={<ProtectedRoute><WinnersAssetPage /></ProtectedRoute>} />
          <Route path="/invitecollab" element={gateClosed ? <Navigate to="/winners-assets" replace /> : <ProtectedRoute><AdminRoute><InviteCollab /></AdminRoute></ProtectedRoute>} />
          {/* Accept invite by /invite/:token, pass as query param for AcceptInvite */}
          <Route path="/invite/:token" element={<AcceptInviteWrapper />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/inviteadmin" element={gateClosed ? <Navigate to="/winners-assets" replace /> : <ProtectedRoute><InviteAdmin /></ProtectedRoute>} />
          <Route path="/schoolinfo" element={gateClosed ? <Navigate to="/winners-assets" replace /> : <ProtectedRoute><SchoolInfoCompletionRedirect><SchoolInfo /></SchoolInfoCompletionRedirect></ProtectedRoute>} />
          <Route path="/grantlanding" element={gateClosed ? <Navigate to="/winners-assets" replace /> : <ProtectedRoute><SchoolInfoEditableGuard><GrantLandingPage /></SchoolInfoEditableGuard></ProtectedRoute>} />
          <Route path="/cigform" element={gateClosed ? <Navigate to="/winners-assets" replace /> : <ProtectedRoute><SchoolInfoEditableGuard><CIGForm /></SchoolInfoEditableGuard></ProtectedRoute>} />
          <Route path="/ilgform" element={gateClosed ? <Navigate to="/winners-assets" replace /> : <ProtectedRoute><SchoolInfoEditableGuard><ILGForm /></SchoolInfoEditableGuard></ProtectedRoute>} />
          <Route path="/cigcomplete" element={gateClosed ? <Navigate to="/winners-assets" replace /> : <ProtectedRoute><SchoolInfoEditableGuard><CIGComplete /></SchoolInfoEditableGuard></ProtectedRoute>} />
          <Route path="/ilgcomplete" element={gateClosed ? <Navigate to="/winners-assets" replace /> : <ProtectedRoute><SchoolInfoEditableGuard><ILGComplete /></SchoolInfoEditableGuard></ProtectedRoute>} />
          <Route path="/faq" element={<FAQ />} />
          {/* Help docs routes — commented out, restore when ready */}
          {/* <Route path="/help" element={<HelpHome />} /> */}
          {/* <Route path="/help/:section/:slug" element={<HelpArticle />} /> */}
          {/* Rules route removed */}
          {/* Catch-all: redirect users based on auth and profile status */}
          <Route path="*" element={<NotFoundHandler gateClosed={gateClosed} />} />
        </Routes>
      </Layout>
    </Router>
  );
  // Wrapper to extract :token param and pass as query param for AcceptInvite
  function AcceptInviteWrapper() {
    const { token } = useParams();
    // Render AcceptInvite with ?token=... in the URL for compatibility
    if (!token) return <Navigate to="/" />;
    window.history.replaceState({}, '', `/invite/${token}?token=${token}`);
    return <AcceptInvite tokenFromPath={token} />;
  }
}

export default App;
